import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  IconAlarm, IconArrowLeft, IconBuilding, IconChecklist, IconChevronLeft, IconCircleCheck, IconCheck, IconDeviceFloppy, IconDots, IconExternalLink, IconFlag, IconHistory, IconInfoSquare, IconLink, IconMail, IconPlus, IconPackage, IconSend, IconTag, IconUsers, IconX,
} from '@tabler/icons-react';
import { DataError, useRecord } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { Av, Card, Kv } from './CustomerDetail';
import { STAGE_LABEL } from './logic';
import { DEAL_JOBS, FOLLOW_OPTS, SVC_ALL, SVC_NAME, SVC_QUOTES, canAck, canAsk, followChoice, inquiryHistory, inquiryInfo, inquiryMails, inquiryTasks, pricingLine, rfqLine, stageActs, stepper, todoRows } from './inquiry';
import type { DealStage } from './types';
import c from './customer-detail.module.css';
import s from './inquiry.module.css';
import { useT } from '@hana/i18n';
import './i18n';

// Mint-Pipeline-FromTask (= Mint-Pipeline d21 · from="task"): chi tiết phiếu 4 tab, trái co giãn · phải 380.
// Chỉ đọc: mọi nút ghi báo toast "Chưa mở ghi ở bản này". Không hiện tiền.

const TABS = [
  { key: 'ov', label: 'crm.iq.tabOv' },
  { key: 'mail', label: 'crm.iq.tabMail' },
  { key: 'task', label: 'crm.iq.tabTask' },
  { key: 'hist', label: 'crm.iq.tabHist' },
] as const;
type Tab = (typeof TABS)[number]['key'];

const STEP_COLOR: Record<DealStage, string> = { New: 'var(--lb)', Pricing: 'var(--d-amber)', Quoted: 'var(--d-blue)', Won: 'var(--d-green)', Lost: 'var(--d-gray)' };
const STAGE_DOT: Record<DealStage, string> = STEP_COLOR;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function Inquiry({ fromTask }: { fromTask: boolean }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { inqId } = useParams({ strict: false }) as { inqId?: string };
  const { data, error, isLoading, isError, refetch } = useRecord('crmDeal', inqId);
  const forced = readState();
  const [tab, setTab] = useState<Tab>('ov');
  const [banner, setBanner] = useState(true);
  const [svcOn, setSvcOn] = useState<string[] | null>(null);
  const [fu, setFu] = useState<string | null>(null);
  const noWrite = () => toast({ msg: t('crm.noWrite') });

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (!data) state = 'empty';
  const ready = state === 'ready' && !!data;
  const d = data ?? null;
  const svc = svcOn ?? d?.services ?? [];
  const fuNow = fu ?? (d ? followChoice(d) : 'Không follow');

  const back = (
    <Link to="/ezy/pipeline" className={c.back} aria-label={t('crm.iq.back')} title={t('crm.iq.back')}>
      <IconChevronLeft size={18} stroke={2} aria-hidden="true" />
    </Link>
  );
  const todo = d ? todoRows(d) : [];

  return (
    <Page kind="table">
      <PageHeader
        title={inqId ?? t('crm.iq.title')}
        findHint={false}
        lead={back}
        meta={ready && d ? (
          <>
            <Chip dot={STAGE_DOT[d.stage]} label={t('crm.iq.stageLbl')}>{STAGE_LABEL[d.stage]}</Chip>
            <Chip label={t('crm.iq.svc')}>{d.services.join(' · ')}</Chip>
          </>
        ) : undefined}
        sub={ready && d ? (
          <span className={c.subRow}>
            <span className={c.subItem} title={t('crm.iq.custOf', { v: d.customer })}><IconBuilding size={14} stroke={1.8} aria-hidden="true" />{d.customer}</span>
            <span className={c.subItem} title={t('crm.iq.followDate')}><IconAlarm size={14} stroke={1.8} aria-hidden="true" />{d.followDays === 0 ? t('crm.iq.today') : d.follow || t('crm.iq.noFollow')}</span>
            <span className={c.subItem} title={t('crm.iq.saleOf', { v: d.sale })}><Av text={d.sale} />{d.sale}</span>
          </span>
        ) : undefined}
        actions={
          <>
            <HeaderTool label={t('crm.iq.more')} icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={<IconDeviceFloppy size={16} aria-hidden="true" />} onClick={noWrite}>{t('crm.iq.save')}</Button>
          </>
        }
      />
      <div className={`${c.page} ${s.page}`}>
        {ready && d && (
          <Segmented label={t('crm.iq.tabsLbl')} value={tab} onChange={setTab} items={TABS.map((x) => ({ key: x.key, label: t(x.label) }))} />
        )}
        {ready && d && fromTask && banner && (
          <div role="status" className={s.banner}>
            <span>{t('crm.iq.bannerMsg', { id: d.id })}</span>
            <button type="button" className={s.bannerBtn} onClick={() => navigate({ to: '/ezy/pipeline/$inqId', params: { inqId: d.id } })}>
              <IconExternalLink size={16} stroke={1.8} aria-hidden="true" />{t('crm.iq.reopen')}
            </button>
            <button type="button" className={s.bannerBtn} onClick={noWrite}>
              <IconArrowLeft size={16} stroke={1.8} aria-hidden="true" />{t('crm.iq.toTasks')}
            </button>
            <button type="button" className={s.bannerX} aria-label={t('crm.iq.closeNote')} onClick={() => setBanner(false)}>×</button>
          </div>
        )}
        <StateView
          state={state === 'ready' && !data ? 'empty' : state}
          title={state === 'empty' || !data ? t('crm.iq.nfTitle') : undefined}
          desc={state === 'empty' || !data ? t('crm.iq.nfDesc') : undefined}
          icon={state === 'empty' || !data ? <IconUsers size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('crm.retry')}</Button> : state === 'empty' ? <Button onClick={() => void navigate({ to: '/ezy/pipeline' })}>{t('crm.iq.toList')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          {d && (
            <div className={c.grid}>
              <div className={c.col}>
                {tab === 'ov' && (
                  <>
                    <Card title={t('crm.iq.cStage')} icon={IconFlag} hint={t('crm.iq.hStage')}>
                      <div className={s.steps}>
                        {stepper(d).map((x) => (
                          <div key={x.stage} className={`${s.step} ${x.state === 'on' ? s.on : ''}`}>
                            <div className={s.bar} style={x.state === 'todo' ? undefined : { background: STEP_COLOR[x.stage] }} />
                            <div>{x.label}</div>
                          </div>
                        ))}
                      </div>
                      <div className={s.acts}>
                        {stageActs(d).map((a) => (
                          <Button key={a.key} variant={a.primary ? 'primary' : 'secondary'} icon={a.key === 'won' || a.key === 'quoted' ? <IconCheck size={16} aria-hidden="true" /> : a.key === 'lost' ? <IconX size={16} aria-hidden="true" /> : undefined} onClick={noWrite}>
                            {a.label}
                          </Button>
                        ))}
                      </div>
                    </Card>
                    <Card title={t('crm.iq.cInfo')} icon={IconInfoSquare} hint={t('crm.iq.hInfo')}>
                      <div className={s.form}>
                        <div><div className={s.fl}>{t('crm.iq.fCust')}</div><Link to="/ezy/customers/$customerId" params={{ customerId: d.customer }} className={`${s.ro} ${s.roLink}`}>{d.customer}</Link></div>
                        <div><div className={s.fl}>{t('crm.iq.fSale')}</div><div className={s.ro}>{d.sale}</div></div>
                        <div className={s.span2}>
                          <div className={s.fl}>{t('crm.iq.svc')}</div>
                          <div className={s.chips}>
                            {SVC_ALL.map((k) => (
                              <button key={k} type="button" className={s.chip} aria-pressed={svc.includes(k)} onClick={() => setSvcOn(svc.includes(k) ? svc.filter((x) => x !== k) : [...svc, k])}>
                                {k} · {SVC_NAME[k]}
                              </button>
                            ))}
                          </div>
                        </div>
                        <label className={s.span2}>
                          <div className={s.fl}>{t('crm.iq.fSubject')} <span className={s.req}>*</span></div>
                          <input className={s.input} defaultValue={d.subject} />
                        </label>
                        <div className={s.span2}>
                          <div className={s.fl}>{t('crm.iq.followDate')}</div>
                          <div role="radiogroup" aria-label={t('crm.iq.followDate')} className={s.fu}>
                            {FOLLOW_OPTS.map((k) => (
                              <button key={k} type="button" role="radio" aria-checked={fuNow === k} onClick={() => setFu(k)}>{k}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Card>
                    <Card
                      title={t('crm.iq.cQuote')}
                      icon={IconTag}
                      hint={t('crm.iq.hQuote')}
                      actions={
                        <>
                          <button type="button" className={s.soft} onClick={noWrite}><IconPlus size={16} stroke={1.8} aria-hidden="true" />{t('crm.iq.attach')}</button>
                          <button type="button" className={s.soft} onClick={noWrite}><IconSend size={16} stroke={1.8} aria-hidden="true" />{t('crm.iq.sendQuote')}</button>
                        </>
                      }
                    >
                      {d.quotes.map((q) => (
                        <div key={q} className={s.row}>
                          <Link to="/ezy/quotes" className={s.rowA}>{q}</Link>
                          <span className={s.sub}>{t('crm.iq.sentCust')}</span>
                        </div>
                      ))}
                      {d.quotes.length === 0 && <div className={s.empty}>{t('crm.iq.noQuote')}</div>}
                    </Card>
                    <Card title={t('crm.iq.cPrice')} icon={IconPackage} hint={t('crm.iq.hPrice')} defaultOpen={false}>
                      <div className={s.cols}>
                        <div>
                          <b>{t('crm.iq.priceSent')}</b>
                          {SVC_QUOTES.map(([k, when]) => <div key={k} className={s.row}><span>{k}</span><span className={s.sub}>{when}</span></div>)}
                        </div>
                        <div>
                          <b>{t('crm.iq.recentJobs')}</b>
                          {(DEAL_JOBS[d.customer] ?? []).map((j, i) => <div key={j} className={s.row}><span>{j}</span><span className={s.sub}>{i ? 'BL Released' : 'BK Confirmed'}</span></div>)}
                        </div>
                      </div>
                    </Card>
                  </>
                )}
                {tab === 'mail' && (
                  <Card title={t('crm.iq.cMail')} icon={IconMail} hint={t('crm.iq.hMail')}>
                    {inquiryMails(d).map((m) => (
                      <div key={m.subject} className={s.who}>
                        <span className={s.mailIc} aria-hidden="true"><IconMail size={15} stroke={1.9} /></span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <b>{m.subject}</b>{m.src && <span className={s.srcTag}>{t('crm.iq.srcMail')}</span>}
                          <small>{m.from} · {m.when}</small>
                        </div>
                        <button type="button" className={s.soft} onClick={noWrite}>{t('crm.iq.open')}</button>
                      </div>
                    ))}
                  </Card>
                )}
                {tab === 'task' && (
                  <Card title={t('crm.iq.cTask')} icon={IconChecklist} hint={t('crm.iq.hTask')}>
                    {inquiryTasks(d).map((k) => (
                      <div key={k.id} className={s.todo}>
                        <input type="checkbox" aria-label={t('crm.iq.doneOf', { v: k.label })} onChange={noWrite} />
                        <div><div>{k.label}</div><div className={s.sub}>{k.due}</div></div>
                        <span className={s.sub}>{k.src}</span>
                      </div>
                    ))}
                    {inquiryTasks(d).length === 0 && <div className={s.empty}>{t('crm.iq.noTask')}</div>}
                  </Card>
                )}
                {tab === 'hist' && (
                  <Card title={t('crm.iq.tabHist')} icon={IconHistory} hint={t('crm.iq.hHist')}>
                    {inquiryHistory(d).map((h) => (
                      <div key={h.text} className={s.hist}>
                        <span className={s.hdot} style={h.stage ? { background: STEP_COLOR[h.stage] } : undefined} />
                        <div><div>{h.text}</div><div className={s.sub}>{h.who} · {h.when}</div></div>
                      </div>
                    ))}
                  </Card>
                )}
              </div>
              <aside className={c.col} aria-label={t('crm.iq.aside')}>
                <Card title={t('crm.iq.cTodo')} icon={IconChecklist} hint={t('crm.iq.hTodo')} actions={todo.length > 0 ? <span className={s.badge}>{todo.length}</span> : undefined}>
                  {todo.map((r) => (
                    <div key={r.key} className={s.todo}>
                      <span className={`${s.todoDot} ${r.tone === 'warn' ? '' : s[r.tone]}`} aria-hidden="true" />
                      <div>
                        <div>{r.label}</div>
                        <div className={`${s.c}`}><Av text={d.sale} /><span className={s.sub}>{d.sale} · {r.due}</span></div>
                      </div>
                      <Button size="sm" icon={<IconCircleCheck size={14} aria-hidden="true" />} onClick={noWrite}>{r.act}</Button>
                    </div>
                  ))}
                  {todo.length === 0 && <div className={s.empty}>{t('crm.iq.noTodo')}</div>}
                </Card>
                <Card title={t('crm.iq.cRfq')} icon={IconSend} hint={t('crm.iq.hRfq')}>
                  <div className={s.rfq}>
                    <b>{t('crm.iq.askPricingH')}</b>
                    <p>{pricingLine(d)}</p>
                    {canAsk(d) && <button type="button" className={s.soft} onClick={noWrite}>{t('crm.iq.askPricing')}</button>}
                    {canAck(d) && <button type="button" className={s.soft} onClick={noWrite}><IconCircleCheck size={16} stroke={1.8} aria-hidden="true" />{t('crm.iq.ackPrice')}</button>}
                  </div>
                  <div className={s.rfq}>
                    <b>{t('crm.iq.askSupH')}</b>
                    <p>{rfqLine(d)}</p>
                    <button type="button" className={s.soft} onClick={noWrite}><IconExternalLink size={16} stroke={1.8} aria-hidden="true" />{t('crm.iq.openPartnerRfq')}</button>
                  </div>
                </Card>
                <Card title={t('crm.iq.cMain')} icon={IconInfoSquare} hint={t('crm.iq.hMain')}>
                  <Kv stack rows={inquiryInfo(d)} />
                </Card>
                <Card title={t('crm.iq.cWho')} icon={IconUsers} hint={t('crm.iq.hWho')}>
                  {[[d.sale, t('crm.iq.fSale')], ['DAVID', t('crm.iq.mgr')]].map(([n, r]) => (
                    <div key={r} className={s.who}><Av text={n!} /><div><b>{n}</b><small>{r}</small></div></div>
                  ))}
                </Card>
                <Card title={t('crm.iq.cLink')} icon={IconLink} hint={t('crm.iq.hLink')}>
                  {[
                    [t('crm.iq.p360', { v: d.customer }), ''],
                    [t('crm.iq.lQuote'), ''],
                    [t('crm.iq.lPartnerRfq'), ''],
                    [t('crm.iq.lTasks'), ''],
                  ].map(([k]) => (
                    <button key={k} type="button" className={c.linkRow} onClick={noWrite}>
                      <span>{k} ↗</span>
                    </button>
                  ))}
                </Card>
              </aside>
            </div>
          )}
        </StateView>
      </div>
    </Page>
  );
}

export const PipelineInquiry = () => <Inquiry fromTask={false} />;
export const PipelineInquiryFromTask = () => <Inquiry fromTask />;
